import { io } from "socket.io-client";

export class Stream {
  websocket: any;
  client: any = null;
  callback: any = {
    error: null,
    subscribe: null,
    register: null,
    message: null,
  };

  manualDisconnect = false;

  constructor(
    private env: {
      host?: any;
      debug?: boolean;
      queryParameters?: any;
      transports?: Array<any>;
    }
  ) {}

  get isConnected() {
    return this.websocket?.connected ?? false;
  }

  get id() {
    return this.websocket.id;
  }

  getInstance() {
    if (this.websocket != null) return this.websocket;

    const setting = {};
    if (this.env.queryParameters) {
      if (JSON.stringify(this.env.queryParameters) != "{}") {
        setting["query"] = this.env.queryParameters;
      }
    }
    if (this.env.transports) {
      if (this.env.transports.length > 0) {
        setting["transports"] = this.env.transports;
      }
    }

    if (this.env.hasOwnProperty("host")) {
      const url = new URL(this.env.host);

      this.customConsole("log", "getInstance", setting);
      setting["path"] = url.pathname;
      return io(url.origin, setting);
    }

    setting["path"] = "/stream/socket.io";

    return io(setting);
  }

  async connect() {
    this.manualDisconnect = false;
    this.websocket = this.getInstance();

    this.onConnection();
    await this.waitUntilConnected();
    this.onDisconnect();
    this.onConnectionError();
    this.onReceived();
    return this;
  }

  private onConnection() {
    const handler = () => {
      if (!this.isConnected) return;
      this.customConsole("log", "connect");
    };
    this.websocket.off("connect");
    this.websocket.on("connect", handler);
  }

  private onDisconnect() {
    const handler = (reason) => {
      this.customConsole("log", "disconnect", reason);
      if (!this.manualDisconnect) {
        this.reconnect();
      }
    };
    this.websocket.off("disconnect");
    this.websocket.on("disconnect", handler);
  }

  private onConnectionError() {
    const handler = (err) => {
      this.customConsole("error", "connect_error", err);
      if (this.callback.error) this.callback.error(err);
    };
    this.websocket.off("connect_error");
    this.websocket.on("connect_error", handler);
  }

  private onReceived() {
    const handler = (evt) => {
      this.customConsole("log", "message =>", evt);
      try {
        var response = evt;
        if (this.callback.subscribe)
          this.callback.subscribe("message", response);

        if (this.callback.message) this.callback.message(response);
      } catch (e) {
        this.customConsole("log", "CHAT ERROR( ONMESSAGE ) => ", e);
      }
    };
    this.websocket.off("message");
    this.websocket.on("message", handler);
  }

  private reconnect() {
    this.disconnect();
    const scope = this;
    setTimeout(function () {
      scope.customConsole("log", "RECONNECTED");
      scope.connect();
    }, 1500);
  }

  register(channel = "public") {
    try {
      const data = {
        type: "register",
        body: {
          channel: channel ? channel : "public",
          id: this.id,
        },
      };
      this.client = data.body;
      if (this.callback.subscribe)
        this.callback.subscribe("connected", data.body);
      if (this.callback.connected) this.callback.connected(data.body);

      this.websocket.emit("register", data);
      this.customConsole("log", "register =>", data);
    } catch (error) {
      this.customConsole("error", "register error =>", error);
    }
  }

  async waitUntilConnected() {
    if (this.isConnected) return;
    return new Promise<void>((resolve) => {
      this.websocket.on("connect", () => {
        if (this.isConnected) resolve();
      });
    });
  }

  private(message) {
    const data = {
      type: "private",
      body: {
        from: this.client.id,
        channel: this.client.channel,
        message: message,
      },
    };

    try {
      this.send(data);
    } catch (error) {
      this.customConsole("error", "private error =>", error);
    }
  }

  public(message) {
    var data = {
      type: "public",
      body: {
        from: this.client?.id,
        channel: "public",
        message: message,
      },
    };

    try {
      this.send(data);
    } catch (error) {
      this.customConsole("error", "public error =>", error);
    }
  }

  private send(data) {
    this.customConsole("log", "send =>", data);
    return this.websocket.emit("message", data);
  }

  private customConsole(type, ...message: any) {
    if (!this.env.hasOwnProperty("debug")) return;

    if (this.env.debug) console[type](...message);
  }

  subscribe(callback) {
    this.callback.subscribe = callback;
    return this;
  }

  connected(callback) {
    this.callback.register = callback;
    return this;
  }

  message(callback) {
    this.callback.message = callback;
    return this;
  }

  catch(callback) {
    this.callback.error = callback;
    return this;
  }

  disconnect() {
    this.manualDisconnect = true;
    if (this.websocket?.removeAllListeners) {
      this.websocket.removeAllListeners();
    }
    if (this.websocket?.disconnect) {
      this.websocket.disconnect();
    }

    this.websocket = null;
  }

  disconnectChannel(channel) {
    this.websocket.emit("disconnect", channel);
  }
}
